/**
 * api.ts - Shared TypeScript type definitions for API
 *
 * These types mirror the backend models and define the shape
 * of data sent to and received from the API.
 */

export interface ByeRequest {
  points: number; // 0.0, 0.5, or 1.0
}

export interface Player {
  id: string;
  firstName: string;
  lastName: string;
  rating?: number;
  ratingStd?: number;    // FIDE Standard/Classical rating
  ratingRapid?: number;  // FIDE Rapid rating
  ratingBlitz?: number;  // FIDE Blitz rating
  title?: string;
  club?: string;
  federation?: string;
  fideId?: string;
  federationCode?: string;
  initialRanking?: number;
  byeRequests?: Record<number, ByeRequest>; // roundNumber -> ByeRequest
  userId?: string; // Linked user account for authentication
  birthDate?: string; // Player's birth date (YYYY-MM-DD) for age-based categories
  gender?: 'M' | 'F'; // Player's gender for gender-based categories
  categories?: string[]; // Array of category IDs this player belongs to
  present?: boolean; // Whether the player has checked in for the tournament
  canEnterResults?: boolean; // Whether this player can enter their own results
  feePaid?: boolean; // Whether the entry fee has been paid
  paidAmount?: number; // Amount paid in cents
  paidCurrency?: string; // Currency of payment (e.g., 'USD', 'CHF')
  paymentSource?: 'stripe' | 'manual'; // Source of payment - Stripe payments cannot be cleared
}

// Category definition for tournament-specific player groupings
export interface Category {
  id: string;
  tournamentId: string;
  name: string; // e.g., "U10", "U12", "Women", "50+"
  type: 'age' | 'gender' | 'title' | 'custom';
  // For age-based categories
  minAge?: number;
  maxAge?: number;
  // For gender-based categories
  genderFilter?: 'M' | 'F';
  // For title-based categories (e.g., non-titled)
  titleFilter?: 'none' | 'any' | string;
  createdAt?: string;
}

export interface Match {
  id: string;
  round: number;
  boardNumber?: number;
  player1: Player;
  player2: Player;
  winner?: string; // player id
  result?: string; // '1-0', '0-1', '0.5-0.5', '1-0F', '0-1F'
  isBye?: boolean;
}

// Tiebreak criteria types - full list from TieBreakServer
export type TiebreakCriterion =
  // Primary tiebreaks (most common)
  | 'buchholz'           // BH - Buchholz (sum of opponent scores)
  | 'buchholzCut1'       // BH with lowest cut
  | 'buchholzCut2'       // BH with 2 lowest cut
  | 'buchholzMedian'     // FB - Buchholz median (cut highest and lowest)
  | 'buchholzSum'        // @deprecated Alias for buchholzMedian
  | 'sonnebornBerger'    // SB - Sonneborn-Berger
  | 'directEncounter'    // DE - Direct encounter (head-to-head)
  | 'wins'               // WIN - Number of wins
  | 'gamesWithBlack'     // BPG - Number of games played with black
  | 'winsWithBlack'      // BWG - Number of wins with black
  // Extended tiebreaks
  | 'progressiveScore'   // PS - Progressive scores (cumulative)
  | 'koya'               // KS - Koya system (% against top half)
  | 'averageRatingOpp'   // ARO - Average rating of opponents
  | 'performanceRating'  // TPR - Tournament performance rating
  // Less common
  | 'rating'             // RTG - Initial rating
  | 'startNumber'        // SNO - Start/pairing number
  | 'initialRank'        // RANK - Initial ranking
  // Round Robin specific
  | 'extendedDE'         // EDE - Extended direct encounter
  // Team tournament specific
  | 'matchPoints'        // MPTS - Match points (team)
  | 'gamePoints'         // GPTS - Game points (team)
  | 'boardCount';        // BC - Board count

// Tiebreak definitions for UI
export interface TiebreakDefinition {
  key: TiebreakCriterion;
  nameKey: string;
  abbrev: string;
  recommendedFor: ('swiss' | 'round-robin' | 'knockout' | 'team')[];
}

// All available tiebreaks - sorted by priority
export const TIEBREAK_DEFINITIONS: Record<TiebreakCriterion, TiebreakDefinition> = {
  buchholz: { key: 'buchholz', nameKey: 'tournament.tiebreak.buchholz', abbrev: 'Bh', recommendedFor: ['swiss'] },
  buchholzCut1: { key: 'buchholzCut1', nameKey: 'tournament.tiebreak.buchholzCut1', abbrev: 'Bh-1', recommendedFor: ['swiss'] },
  buchholzCut2: { key: 'buchholzCut2', nameKey: 'tournament.tiebreak.buchholzCut2', abbrev: 'Bh-2', recommendedFor: ['swiss'] },
  buchholzMedian: { key: 'buchholzMedian', nameKey: 'tournament.tiebreak.buchholzMedian', abbrev: 'BhM', recommendedFor: ['swiss'] },
  buchholzSum: { key: 'buchholzSum', nameKey: 'tournament.tiebreak.buchholzMedian', abbrev: 'BhM', recommendedFor: [] }, // deprecated
  sonnebornBerger: { key: 'sonnebornBerger', nameKey: 'tournament.tiebreak.sonnebornBerger', abbrev: 'SB', recommendedFor: ['swiss', 'round-robin'] },
  directEncounter: { key: 'directEncounter', nameKey: 'tournament.tiebreak.directEncounter', abbrev: 'DE', recommendedFor: ['swiss', 'round-robin'] },
  wins: { key: 'wins', nameKey: 'tournament.tiebreak.wins', abbrev: 'W', recommendedFor: ['swiss', 'round-robin'] },
  gamesWithBlack: { key: 'gamesWithBlack', nameKey: 'tournament.tiebreak.gamesWithBlack', abbrev: 'BkG', recommendedFor: ['swiss'] },
  winsWithBlack: { key: 'winsWithBlack', nameKey: 'tournament.tiebreak.winsWithBlack', abbrev: 'BkW', recommendedFor: ['swiss'] },
  progressiveScore: { key: 'progressiveScore', nameKey: 'tournament.tiebreak.progressiveScore', abbrev: 'PS', recommendedFor: ['swiss'] },
  koya: { key: 'koya', nameKey: 'tournament.tiebreak.koya', abbrev: 'Koya', recommendedFor: ['round-robin'] },
  averageRatingOpp: { key: 'averageRatingOpp', nameKey: 'tournament.tiebreak.averageRatingOpp', abbrev: 'ARO', recommendedFor: ['swiss'] },
  performanceRating: { key: 'performanceRating', nameKey: 'tournament.tiebreak.performanceRating', abbrev: 'TPR', recommendedFor: ['swiss'] },
  rating: { key: 'rating', nameKey: 'tournament.tiebreak.rating', abbrev: 'Rtg', recommendedFor: ['swiss', 'round-robin'] },
  startNumber: { key: 'startNumber', nameKey: 'tournament.tiebreak.startNumber', abbrev: 'SNO', recommendedFor: [] },
  initialRank: { key: 'initialRank', nameKey: 'tournament.tiebreak.initialRank', abbrev: 'Rk', recommendedFor: [] },
  extendedDE: { key: 'extendedDE', nameKey: 'tournament.tiebreak.extendedDE', abbrev: 'EDE', recommendedFor: ['round-robin'] },
  matchPoints: { key: 'matchPoints', nameKey: 'tournament.tiebreak.matchPoints', abbrev: 'MP', recommendedFor: ['team'] },
  gamePoints: { key: 'gamePoints', nameKey: 'tournament.tiebreak.gamePoints', abbrev: 'GP', recommendedFor: ['team'] },
  boardCount: { key: 'boardCount', nameKey: 'tournament.tiebreak.boardCount', abbrev: 'BC', recommendedFor: ['team'] },
};

// Get all tiebreak keys in priority order (for UI display)
export const ALL_TIEBREAK_KEYS: TiebreakCriterion[] = [
  'buchholz', 'buchholzCut1', 'buchholzMedian', 'sonnebornBerger', 'directEncounter',
  'wins', 'progressiveScore', 'gamesWithBlack', 'winsWithBlack',
  'averageRatingOpp', 'performanceRating', 'koya', 'extendedDE', 'rating',
  'initialRank', 'startNumber'
];

// Get tiebreaks recommended for a format
export function getRecommendedTiebreaks(format: 'swiss' | 'round-robin' | 'knockout'): TiebreakCriterion[] {
  return ALL_TIEBREAK_KEYS.filter(key => {
    const def = TIEBREAK_DEFINITIONS[key];
    return def && def.recommendedFor.includes(format);
  });
}

// Default tiebreaks by format
export const DEFAULT_TIEBREAKS_BY_FORMAT: Record<string, TiebreakCriterion[]> = {
  'swiss': ['buchholz', 'buchholzCut1', 'sonnebornBerger', 'directEncounter', 'wins'],
  'round-robin': ['directEncounter', 'sonnebornBerger', 'wins', 'koya', 'rating'],
  'knockout': ['wins'],
};

export const DEFAULT_TIEBREAK_CRITERIA: TiebreakCriterion[] = DEFAULT_TIEBREAKS_BY_FORMAT['swiss'];
export const MAX_TIEBREAK_CRITERIA = 5;

export interface Standing {
  player: Player;
  points: number;
  matchesPlayed: number;
  wins: number;
  draws: number;
  losses: number;
  rank: number;
  // Dynamic tiebreak scores
  tiebreakScores?: Record<string, number>;
  // Legacy fields for backwards compatibility
  buchholz?: number;
  buchholzSum?: number;
  sonnebornBerger?: number;
  directEncounter?: number;
  progressiveScore?: number;
  koya?: number;
  averageRatingOpp?: number;
  performanceRating?: number;
}

export interface Round {
  id: string;
  tournamentId: string;
  roundNumber: number;
  roundDate?: string;
  startTime?: string;
}

export interface TournamentShare {
  id: string;
  tournamentId: string;
  userId: string;
  sharedBy: string;
  createdAt: string;
}

export type TournamentFormat = 'round-robin' | 'swiss' | 'knockout';
export type TournamentStatus = 'upcoming' | 'ongoing' | 'completed';

export interface Tournament {
  id: string;
  name: string;
  description: string;
  venue?: string;
  logo?: string;
  gameCadence?: string;
  startDate: string;
  endDate: string;
  format: TournamentFormat;
  players: Player[];
  matches: Match[];
  status: TournamentStatus;
  currentRound?: number;
  numberOfRounds?: number;
  doubleRoundRobin?: boolean;
  initialColor?: 'white' | 'black' | 'random';
  tiebreakCriteria?: TiebreakCriterion[]; // Custom tiebreak order
  userId?: string;
  shareToken?: string;
  pairingLogs?: string;
  sharedWith?: Array<{userId: string; username: string}>;
  sharedBy?: string;
  subscriptionTier?: 'free' | 'pro';
  published?: boolean;
  allowSelfRegistration?: boolean;
  allowUserResultEntry?: boolean;
  location?: string;
  latitude?: number;
  longitude?: number;
  timeControl?: string;
  isRated?: boolean;
  stripeConnectedAccountId?: string;
  hasEntryFees?: boolean;
}

// API Request/Response types
export interface CreateTournamentRequest {
  name: string;
  description: string;
  venue?: string;
  logo?: string;
  gameCadence?: string;
  startDate: string;
  endDate: string;
  format: TournamentFormat;
  numberOfRounds?: number;
  doubleRoundRobin?: boolean;
  initialColor?: 'white' | 'black' | 'random';
  tiebreakCriteria?: TiebreakCriterion[];
}

export interface UpdateTournamentRequest {
  name?: string;
  description?: string;
  venue?: string;
  logo?: string;
  gameCadence?: string;
  startDate?: string;
  endDate?: string;
  status?: TournamentStatus;
  numberOfRounds?: number;
  doubleRoundRobin?: boolean;
  initialColor?: 'white' | 'black' | 'random';
  tiebreakCriteria?: TiebreakCriterion[];
}

export interface AddPlayerRequest {
  firstName: string;
  lastName: string;
  rating?: number;
  title?: string;
  club?: string;
  federation?: string;
  fideId?: string;
  federationCode?: string;
  initialRanking?: number;
  birthDate?: string;
  gender?: 'M' | 'F';
}

export interface UpdatePlayerRequest {
  firstName?: string;
  lastName?: string;
  rating?: number;
  title?: string;
  club?: string;
  federation?: string;
  fideId?: string;
  federationCode?: string;
  initialRanking?: number;
  birthDate?: string;
  gender?: 'M' | 'F';
}

export interface AddMatchRequest {
  round: number;
  player1Id: string;
  player2Id: string;
  result?: string;
  winnerId?: string;
  isBye?: boolean;
}

export interface UpdateMatchResultRequest {
  result: string;
  winnerId?: string | null;
}

export interface GeneratePairingsRequest {
  roundDate?: string;
  startTime?: string;
}

export interface UpdateRoundScheduleRequest {
  roundDate?: string;
  startTime?: string;
}

export interface ShareLinkResponse {
  shareToken: string;
  shareUrl: string;
}

export interface StandingsResponse {
  standings: Standing[];
  tournament: Tournament;
}

export interface PlayerSearchResult {
  name: string;
  rating?: number;
  title?: string;
  federation?: string;
  fideId?: string;
}

// Prohibited pairing - players who cannot be paired against each other
export interface ProhibitedPairing {
  id: string;
  tournamentId: string;
  player1Id: string;
  player2Id: string;
  reason?: string;
  createdAt?: string;
  // Enriched fields from API
  player1Name?: string;
  player2Name?: string;
  otherPlayerId?: string;
  otherPlayerName?: string;
}

export interface ApiError {
  error: string;
  details?: any;
}
